import { useEffect, useRef } from "react";

function mulberry(seed) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function Starfield({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const rand = mulberry(20260908);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let w = 0;
    let h = 0;
    let raf;
    let constellations = [];
    let meteors = [];
    let nextMeteor = performance.now() + 7000 + rand() * 9000;

    const build = () => {
      constellations = [];
      // 5 zones éparses, constellations de 4 à 7 étoiles
      const zones = [
        [0.10, 0.16],
        [0.34, 0.10],
        [0.58, 0.24],
        [0.84, 0.14],
        [0.68, 0.62],
      ];
      zones.forEach(([zx, zy]) => {
        const n = 4 + Math.floor(rand() * 4);
        const stars = [];
        let x = zx * w;
        let y = zy * h;
        for (let i = 0; i < n; i++) {
          stars.push({ x, y, r: 0.6 + rand() * 1.1, phase: rand() * Math.PI * 2, speed: 0.25 + rand() * 0.45 });
          x += (0.2 + rand() * 0.8) * w * 0.055 * (rand() > 0.25 ? 1 : -1);
          y += (rand() - 0.5) * h * 0.11;
        }
        constellations.push(stars);
      });
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
      if (reduced) drawStatic();
    };

    const drawConstellations = (time) => {
      for (const stars of constellations) {
        ctx.beginPath();
        stars.forEach((s, i) => (i ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y)));
        ctx.strokeStyle = "rgba(243,240,234,0.045)";
        ctx.lineWidth = 0.6;
        ctx.stroke();
        for (const s of stars) {
          const tw = 0.5 + 0.5 * Math.sin(time * s.speed + s.phase);
          const alpha = 0.05 + tw * 0.11;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(197,160,89,${alpha * 0.14})`;
          ctx.fill();
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(243,240,234,${alpha})`;
          ctx.fill();
        }
      }
    };

    const drawStatic = () => {
      ctx.clearRect(0, 0, w, h);
      drawConstellations(0);
    };

    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      drawConstellations(t / 1000);

      if (t > nextMeteor && meteors.length === 0) {
        const dir = rand() > 0.5 ? 1 : -1;
        const slope = 0.35 + rand() * 0.4;
        meteors.push({
          x: w * (0.15 + rand() * 0.7),
          y: h * (0.04 + rand() * 0.28),
          vx: dir * (6 + rand() * 4),
          vy: (6 + rand() * 4) * slope,
          life: 0,
          maxLife: 50 + rand() * 30,
        });
        nextMeteor = t + 10000 + rand() * 16000;
      }
      meteors = meteors.filter((m) => m.life < m.maxLife && m.y < h + 40);
      for (const m of meteors) {
        m.life += 1;
        m.x += m.vx;
        m.y += m.vy;
        const fade = Math.sin((m.life / m.maxLife) * Math.PI);
        const grad = ctx.createLinearGradient(m.x, m.y, m.x - m.vx * 11, m.y - m.vy * 11);
        grad.addColorStop(0, `rgba(243,240,234,${0.3 * fade})`);
        grad.addColorStop(1, "rgba(243,240,234,0)");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.1;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(m.x, m.y);
        ctx.lineTo(m.x - m.vx * 11, m.y - m.vy * 11);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };

    resize();
    if (!reduced) raf = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="starfield-canvas"
      aria-hidden="true"
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}
